import { createModel } from '@rematch/core';
import { Account, PartialAccount } from 'domains/accountDomain';
import { Paged } from 'domains/appDomain';
import { SiteRoleEnum } from 'domains/roleDomain';
import type { RootModel } from 'models';
import { baseReducers, BaseState } from 'models/baseReducers';
import accountService from 'services/accountService';
import { Contact, ContactAddress } from './../domains/orgDomain';

interface AccountState extends BaseState {
  pagedData?: Paged<Account>;
  allAccounts?: Account[];
  accountWithLink?: Account;
  size: number;
  email: string;
  page: number;
  curItem?: PartialAccount;
  roleData: any[];
  token?: string;
  tokenType?: string;
  loginRedirect?: string;
  curContact?: Contact;
  curAddr?: ContactAddress;
}

export const account = createModel<RootModel>()({
  state: {
    pagedData: undefined,
    allAccounts: undefined,
    size: 20,
    page: 1,
    curItem: undefined,
    email: '',
    accountWithLink: undefined,
  } as AccountState,
  reducers: {
    ...baseReducers<AccountState>(),
  },
  effects: (dispatch) => {
    const { account } = dispatch;
    return {
      async getPageUser(payload, state): Promise<void> {
        const pagedData = await accountService.getPagedAccount({
          size: state.account.size,
          page: state.account.page,
          email: state.account.email,
        });
        if (!pagedData.content.length && state.account.page !== 1) {
          account.setState({
            page: 1,
          });
          await account.getPageUser();
        } else {
          account.setState({
            pagedData: pagedData,
          });
        }
      },

      async getAllAccounts(): Promise<void> {
        let accounts = await accountService.getAllAccounts();
        account.setState({ allAccounts: accounts });
      },

      async saveUser(payload: PartialAccount): Promise<Account> {
        const user = await accountService.addUser(payload);
        await account.getPageUser();
        return user;
      },

      async deleteUser(payload: PartialAccount): Promise<void> {
        await accountService.deleteUser(payload);

        await account.getPageUser();
      },

      async updateUser(payload: PartialAccount): Promise<void> {
        await accountService.updateUser(payload);

        await account.getPageUser();
      },

      async renewAccount(payload: PartialAccount): Promise<void> {
        await accountService.renew(payload);
      },

      async activateAccount(payload: PartialAccount): Promise<void> {
        await accountService.activate(payload);
      },

      async resetPassword(payload: PartialAccount): Promise<void> {
        await accountService.resetPassword(payload);
      },

      async resendActivation(payload: PartialAccount): Promise<void> {
        const accountWithLink = await accountService.resendActivation(payload);
        account.setState({ accountWithLink });
      },

      setToken(payload: {
        token: string | undefined;
        tokenType: string | undefined;
      }) {
        account.setState({
          token: payload.token,
          tokenType: payload.tokenType,
        });
      },

      async switchUser(payload: SiteRoleEnum) {
        const res = await accountService.switchUser(payload);
        localStorage.setItem('Authorization', res);
        return res;
      },

      setRedirect(payload: string | undefined) {
        account.setState({ loginRedirect: payload });
      },

      setSearchEmail(email: string) {
        account.setState({
          email,
        });
      },
    };
  },
});
